(ns app.front.view.public-records.licences
  "Public Records Licences tab content"
  (:require [app.domain.municipality.model :as municipality-model]
            [app.domain.util :as du]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.view.public-records.common :as pr-common]
            [app.front.view.public-records.ui :as prui]
            [app.i18n.model :as i18n-model]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;; ---- Data path interceptor --------------------------------------------------

(def data-path (rf/path ::data))

;; ---- Events -----------------------------------------------------------------

(rf/reg-event-fx ::ensure-init
  (fn [{:keys [db]} [_ branding]]
    (let [{current-branding :branding
           initialized?     :initialized?} (::data db)]
      (when (or (not initialized?)
                (not= current-branding branding))
        {:dispatch [::init branding]}))))

(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ branding]]
    {:db (assoc db ::data {:branding         branding
                           :initialized?     true
                           :results          []
                           :visible-filters  nil
                           :selected-options {}
                           :sort-condition   (pr-common/default-sort-for-tab :public-records/licences)
                           :skip             0
                           :limit            20
                           :loading-more?    false
                           :total-count      0})
     :fx [[:dispatch [::search]]]}))

(defn- licence-search-options [selected-options]
  (let [{:keys [municipalities categories]} (pr-common/selected-options->search-options selected-options)]
    (p/assoc-if {}
                :municipalities municipalities
                :category-ids categories)))

(rf/reg-event-fx ::search
  (fn [{:keys [db]} [_ {:keys [keep-results? sort-condition]}]]
    (let [data-db                                        (::data db)
          actual-sort                                    (or sort-condition (:sort-condition data-db))
          new-skip                                       (pr-common/search-skip (:skip data-db) keep-results?)
          updated-data-db                                (cond-> (assoc data-db
                                                                        :skip new-skip
                                                                        :loading-more? (boolean keep-results?))
                                                           actual-sort (assoc :sort-condition actual-sort))
          updated-db                                     (assoc db ::data updated-data-db)
          {:keys [branding selected-options limit skip]} (::data updated-db)]
      {:db updated-db
       :fx [[:dispatch [:comm/command-monitored
                        {:command    :public-records/search-licences
                         :method     :post
                         :data       (cond-> (merge {:branding branding
                                                     :skip     skip
                                                     :limit    limit}
                                                    (licence-search-options selected-options))
                                       actual-sort (assoc :sort-condition actual-sort))
                         :on-success [::search.success keep-results?]}]]]})))

(defn- visible-filter-configs [available-filters]
  [{:attribute          :categories
    :visibility-options (:categories available-filters)}
   {:attribute          :municipalities
    :visibility-options (:municipalities available-filters)}])

(defn- search-filter-configs [lang selected-options municipalities categories]
  [{:attribute    :categories
    :id           "licence-category-filter"
    :options      categories
    :value        (:categories selected-options)
    :on-change    #(>evt [::set-selected-options :categories %])
    :label-tr-key :public-records/category
    :val->str     #(i18n-model/get-text-from-entities-or-map (:group.config/names %) lang)}
   {:attribute    :municipalities
    :id           "licence-municipalities-filter"
    :options      (or municipalities [])
    :value        (:municipalities selected-options)
    :on-change    #(>evt [::set-selected-options :municipalities %])
    :label-tr-key :public-records/municipality
    :val->str     #(municipality-model/municipality-name % lang)}])

(rf/reg-event-fx ::search.success
  data-path
  (fn [{:keys [db]} [_ keep-results? _status {:keys [total-count results available-filters]}]]
    (let [selected-options    (:selected-options db)
          pruned-options      (pr-common/prune-selected-options selected-options available-filters)
          visible-filters     (pr-common/initial-visible-filters (:visible-filters db)
                                                                 (visible-filter-configs available-filters)
                                                                 pruned-options)
          selections-changed? (not= selected-options pruned-options)]
      {:db (assoc db
                  :total-count total-count
                  :available-filters available-filters
                  :visible-filters visible-filters
                  :selected-options pruned-options
                  :results (pr-common/merge-search-results (:results db)
                                                           results
                                                           keep-results?))
       :fx (cond-> []
             selections-changed?
             (conj [:dispatch [::search {:keep-results? false}]]))})))

(rf/reg-event-fx ::set-selected-options
  data-path
  (fn [{:keys [db]} [_ attribute options]]
    (let [selected-options (if (seq options)
                             (assoc (:selected-options db) attribute options)
                             (dissoc (:selected-options db) attribute))]
      {:db (assoc db
                  :selected-options selected-options
                  :skip             0
                  :results          [])
       :fx [[:dispatch [::search]]]})))

(rf/reg-event-fx ::load-more
  data-path
  (pr-common/make-load-more-handler {:search-evt ::search}))

;; ---- Subscriptions ----------------------------------------------------------

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::available-filters
  :<- [::data]
  :-> :available-filters)

(rf/reg-sub ::categories
  :<- [::available-filters]
  (fn [available-filters]
    (not-empty (:categories available-filters))))

(rf/reg-sub ::municipality-options
  :<- [::available-filters]
  :<- [::pr-common/municipalities]
  (pr-common/make-municipality-options-sub))

(rf/reg-sub ::selected-options
  :<- [::data]
  (fn [data [_ attribute]]
    (set (get-in data [:selected-options attribute]))))

(rf/reg-sub ::visible-filters
  :<- [::data]
  (fn [data _]
    (or (:visible-filters data) #{})))

(rf/reg-sub ::results
  :<- [::data]
  :-> :results)

(rf/reg-sub ::total-count
  :<- [::data]
  :-> :total-count)

(rf/reg-sub ::loading-more?
  :<- [::data]
  :-> :loading-more?)

(rf/reg-sub ::sort-condition
  :<- [::data]
  :-> :sort-condition)

(rf/reg-sub ::columns
  :<- [::municipality-options]
  :<- [:tr]
  :<- [:language]
  :<- [::date-util/date-fn]
  (fn [[municipalities tr lang format-date] _]
    (let [municipality-number->name (->> municipalities
                                         (map (fn [m]
                                                [(:municipality/number m)
                                                 (municipality-model/municipality-name m lang)]))
                                         (into {}))]
      (into []
            (concat
              (when (du/at-least-two? municipalities)
                [[(tr [:public-records/municipality])
                  #(->> % :municipality/number (get municipality-number->name))]])
              [[(tr [:licence/id])
                :licence/id]
               [(tr [:public-records/holder])
                (fn [result]
                  (i18n-model/get-text-from-entities-or-map (:holder-names result) lang))]
               [(tr [:public-records/category])
                (fn [result]
                  (i18n-model/get-text-from-entities-or-map (:category-names result) lang))]
               [(tr [:public-records/address])
                :address]
               [(tr [:licence/valid-from])
                (fn [result]
                  (some-> (:licence/valid-from result) format-date))
                {:sorting            (fn [a b]
                                       (compare (:licence/valid-from a)
                                                (:licence/valid-from b)))
                 :sort-attribute-key :licence/valid-from
                 :on-sort-hook       (partial pr-common/on-sort-hook ::search :licence/valid-from)}]
               [(tr [:licence/valid-to])
                (fn [result]
                  (some-> (:licence/valid-to result) format-date))
                {:sorting            (fn [a b]
                                       (compare (:licence/valid-to a)
                                                (:licence/valid-to b)))
                 :sort-attribute-key :licence/valid-to
                 :on-sort-hook       (partial pr-common/on-sort-hook ::search :licence/valid-to)}]
               [(tr [:public-records/state])
                (fn [result]
                  (tr [(:domain/state result)]))]])))))

(rf/reg-sub ::initial-sorting
  :<- [::sort-condition]
  :<- [::columns]
  (fn [[sort-condition columns] _]
    (pr-common/sort-condition->initial-sorting sort-condition columns)))

;; ---- Views ------------------------------------------------------------------

(defn- search-filter [tr]
  (let [lang                    (<sub [:language])
        municipalities          (<sub [::municipality-options])
        categories              (or (<sub [::categories]) [])
        visible-filters         (<sub [::visible-filters])
        selected-municipalities (<sub [::selected-options :municipalities])
        selected-categories     (<sub [::selected-options :categories])]
    [pr-common/render-search-filters tr
     visible-filters
     (search-filter-configs lang
                            {:municipalities selected-municipalities
                             :categories     selected-categories}
                            municipalities
                            categories)]))

(defn- search-result-table [tr]
  [sortable-table/table
   {:id              "licence-search-results"
    :cols            (<sub [::columns])
    :configurable?   true
    :id-func         :id
    :initial-sorting (<sub [::initial-sorting])
    :items           (<sub [::results])
    :tr              tr
    :sticky-header?  true}])

(defn tab-view []
  (let [tr              (<sub [:tr])
        visible-filters (<sub [::visible-filters])
        total-count     (<sub [::total-count])
        in-progress?    (<sub [:comm/command-processing? :public-records/search-licences])]
    [:div
     [search-filter tr]
     [:div {:class (if (seq visible-filters) "mt-8 mb-4" "mb-4")}
      [prui/results-count tr total-count in-progress?]]
     [pr-common/render-paginated-table
      {:tr            tr
       :in-progress?  in-progress?
       :loading-more? (<sub [::loading-more?])
       :results       (<sub [::results])
       :total-count   total-count
       :table         [search-result-table tr]
       :load-more-evt ::load-more}]]))
